import * as babylon from "@babylonjs/core";

import { isAbortError } from "@/shared/errors";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import { LoggerService } from "@/shared/services/LoggerService";

import {
  applyPointCloudDisplay,
  createPointCloudMesh,
  uploadPointCloudFrame,
} from "../../assetBuilders/pointCloud";
import * as components from "../../components";
import {
  markLoadFailed,
  type AsyncReloadable,
} from "../../components/reloadable";
import { BabylonContext, BabylonState, Entity } from "../../state";
import { logTimeBinarySearch } from "../render/setPositionsToTime";

/**
 * Services every point cloud on the 50 ms poll, in three steps per entity:
 *
 * 1. *Index*: a stale component loads its topic's log-time index, once.
 * 2. *Fetch*: a wanted frame the cache holds is uploaded on this poll, with no
 *    worker round trip. Otherwise, and while the cache has room, ask the
 *    worker for one message: the wanted frame when it is missing, else the
 *    first message after it the cache lacks. The cache's room is what bounds
 *    reading ahead: it fills in two buckets, and drops the one playback has
 *    played through (`PointCloudFrameCache`), so during playback the reads
 *    run on ahead of the clock for as long as it does. At most one fetch is
 *    ever in flight, and a newer want waits for it rather than aborting it: a
 *    fetch may be filling the reader's cache (the first Parquet read
 *    downloads a whole column), and aborting on every scrub step would
 *    restart that work each time and let no frame through during playback.
 *    What a fetch brings always lands in the cache, wanted or not, and goes
 *    up only if it moves the mesh toward the frame wanted by the time it
 *    lands. Nothing wanted hides the mesh and does drop the fetch, since
 *    there is nothing to show.
 * 3. *Upload*: a frame that has arrived goes into the mesh — built on first
 *    use. The scene generation is bumped when the frame reaches past the
 *    extent the cloud has shown so far, or when the cloud comes back into
 *    view, since that is when its contribution to the scene changed; a frame
 *    inside that extent is the same scene to the camera, and bumping for it
 *    would re-measure the envelope and rebuild the grid on every message. A
 *    display-only spec change re-dresses the mesh here without a refetch.
 *
 * What a failed fetch means depends on whether any frame has landed before it.
 * Before one has, the cloud cannot be read at all — most often the bound topic
 * is not a PointCloud, which no retry fixes — so the index load, the one load
 * the cloud has, ends `"failed"` as a position's cache does when its fetch
 * throws (`markLoadFailed`): nothing more is fetched, and only a data-spec
 * change, which replaces the component, tries again. An index that cannot be
 * read fails the same way. Once a frame has landed, the failure is one
 * message's: a read that failed in transit, or a message the decoder rejects.
 * Fetches ahead skip that log time from then on; if it was the wanted frame,
 * nothing is drawn there until it is wanted again, which asks once more. Every
 * other frame still plays. A read the data server aborts itself — the reader
 * cancels a background download when another read needs the file sooner — is
 * not a failure at all: the next poll asks again.
 */
export function reloadStalePointClouds(state: BabylonState) {
  for (const entity of Object.values(state.entities)) {
    const cloud = entity.pointCloud;
    if (!cloud) {
      continue;
    }
    reloadIndex(cloud, state.context);
    fetchFrames(cloud, state);
    uploadPendingFrame(entity, cloud, state);
  }
}

function reloadIndex(cloud: components.PointCloud, context: BabylonContext) {
  if (cloud.index.load.state !== "stale") {
    return;
  }
  cloud.index.load.abortController.abort();
  const abortController = new AbortController();
  const load: AsyncReloadable = { state: "loading", abortController };
  cloud.index.load = load;

  context.dataServer
    .getLogTimes(cloud.spec.source.topic.id, abortController.signal)
    .then((logtimes) => {
      abortController.signal.throwIfAborted();
      cloud.index.logtimes = logtimes;
      load.state = "loaded";
    })
    .catch((e: unknown) => {
      if (isAbortError(e)) {
        if (!abortController.signal.aborted) {
          // Aborted by the data server, not by this component: the reader
          // cancels a background download when another read needs the file
          // sooner. The topic is fine, so the next poll asks again.
          load.state = "stale";
        }
        return;
      }
      ErrorMonitoringService.captureError(e);
      if (abortController.signal.aborted) {
        // Superseded or torn down: whatever aborted the load owns the
        // component now, and this failure says nothing about it.
        return;
      }
      // With no log times there is no message to ask for, so the cloud can
      // never draw.
      markLoadFailed(load, abortController.signal);
    });
}

function fetchFrames(cloud: components.PointCloud, state: BabylonState) {
  const context = state.context;
  if (cloud.index.load.state !== "loaded") {
    return;
  }
  const wanted = cloud.wanted;

  if (wanted === undefined) {
    // Before the first message: nothing to draw, and nothing worth fetching.
    if (cloud.fetch) {
      cloud.fetch.abortController.abort();
      cloud.fetch = undefined;
    }
    cloud.pending = undefined;
    if (cloud.mesh && cloud.current) {
      cloud.mesh.setEnabled(false);
      // A cloud that stops showing shrinks the scene as surely as one that
      // starts showing grows it: the camera envelope measures shown clouds
      // only, and cached on the generation it would keep this one's zoom
      // floor over whatever remains. Once, on the edge: `current` is cleared.
      if (cloud.current.frame.pointCount > 0) {
        bumpEntityGeneration(state);
      }
      cloud.current = undefined;
    }
    return;
  }

  if (wanted === cloud.current?.frame.logTime) {
    // Back on the frame on screen before a frame that landed for a later want
    // went up: uploading that one now would show it at a time it is not.
    cloud.pending = undefined;
  } else if (wanted !== cloud.pending?.logTime) {
    // A wanted frame the cache holds is uploaded on this poll. It takes the
    // place of a frame that landed for an older want, which stays cached.
    const cached = cloud.cache.get(wanted);
    if (cached !== undefined) {
      LoggerService.log("[PointCloud] cache hit", { logTime: String(wanted) });
      cloud.pending = cached;
    }
  }

  // One fetch at a time, whatever it was for: what it brings is cached, and
  // the newest want is served on the poll after it lands.
  if (cloud.fetch !== undefined) {
    return;
  }
  const next = nextLogTimeToFetch(cloud, wanted);
  if (next !== undefined) {
    fetchFrame(cloud, context, next, next === wanted);
  }
}

/**
 * The log time to fetch next: the wanted frame while the component does not
 * hold it, else the first message after it that the component neither holds
 * nor has seen fail before — as long as the cache has room for another
 * frame, since one fetched ahead into a full cache would only evict one
 * fetched ahead before it.
 *
 * The scan past the wanted frame stops at the first message it can return,
 * so it walks only the frames held ahead of the playhead, which the cache's
 * budget bounds, plus any that failed.
 */
function nextLogTimeToFetch(
  cloud: components.PointCloud,
  wanted: bigint,
): bigint | undefined {
  const held = (logTime: bigint) =>
    cloud.cache.has(logTime) ||
    logTime === cloud.pending?.logTime ||
    logTime === cloud.current?.frame.logTime;
  if (!held(wanted)) {
    return wanted;
  }
  if (!cloud.cache.canGrow()) {
    return undefined;
  }
  const { logtimes } = cloud.index;
  for (
    let i = logTimeBinarySearch(logtimes, wanted) + 1;
    i < logtimes.length;
    i++
  ) {
    const logTime = logtimes[i];
    if (!held(logTime) && !cloud.failed.has(logTime)) {
      return logTime;
    }
  }
  return undefined;
}

function fetchFrame(
  cloud: components.PointCloud,
  context: BabylonContext,
  logTime: bigint,
  isWanted: boolean,
) {
  const abortController = new AbortController();
  cloud.fetch = { logTime, abortController };
  const startedAt = performance.now();
  const { source, right, forward, up, stride } = cloud.spec;

  context.dataServer
    .getPointCloudAt(
      {
        topicId: source.topic.id,
        messagePathId: source.messagePath.id,
        logTime,
        mapping: { right, forward, up, stride: stride ?? 1 },
      },
      abortController.signal,
    )
    .then((frame) => {
      if (abortController.signal.aborted) {
        return;
      }
      cloud.fetch = undefined;
      // No message at a log time the index listed: a mismatch between index
      // and data that asking again would only repeat. An empty frame draws
      // nothing and, once cached, is not asked for again.
      const landed = frame ?? emptyFrame(logTime);
      cloud.cache.put(landed);
      LoggerService.log("[PointCloud] fetched", {
        logTime: String(logTime),
        ahead: !isWanted,
        ms: Math.round(performance.now() - startedAt),
        points: landed.pointCount,
        cachedFrames: cloud.cache.size,
        cachedMiB: Number((cloud.cache.byteLength / 2 ** 20).toFixed(1)),
      });
      // Shown on the next poll if it moves the mesh toward the wanted frame,
      // and no frame is already waiting to be uploaded: during playback that
      // outruns the fetches, each frame that lands is progress.
      if (cloud.pending === undefined && movesTowardWanted(cloud, logTime)) {
        cloud.pending = landed;
      }
    })
    .catch((e: unknown) => {
      if (isAbortError(e)) {
        if (!abortController.signal.aborted) {
          // Aborted by the data server, not by this component, as for the
          // index: nothing is wrong with the frame, so the next poll asks for
          // whatever is wanted then.
          cloud.fetch = undefined;
        }
        return;
      }
      ErrorMonitoringService.captureError(e);
      if (abortController.signal.aborted) {
        // Dropped before it failed — nothing is wanted, or the component is
        // gone — so the failure concerns nothing on screen.
        return;
      }
      cloud.fetch = undefined;
      if (!anyFrameLanded(cloud)) {
        // The cloud cannot be read at all, so its load fails as a whole: the
        // index load is the one the cloud has, and `fetchFrames` asks for
        // nothing while it is not "loaded". This fetch's own signal stands
        // for the component: it is aborted when the component goes.
        markLoadFailed(cloud.index.load, abortController.signal);
        return;
      }
      cloud.failed.add(logTime);
      if (logTime === cloud.wanted && cloud.pending === undefined) {
        // Drawn as nothing rather than leaving the frame before it up, which
        // is not what is at this time. Not cached, so wanting it again later
        // asks once more: the failure may have been the network's.
        cloud.pending = emptyFrame(logTime);
      }
    });
}

/**
 * Whether showing the frame at `logTime` moves the mesh toward the wanted
 * frame: it is at or before the wanted one — anything after is from after the
 * visualized time — and nearer to it than the frame on screen. A frame that
 * lands for a want the user has since left can fail either test: a scrub back
 * to before it, or back to the frame on screen.
 */
function movesTowardWanted(
  cloud: components.PointCloud,
  logTime: bigint,
): boolean {
  const wanted = cloud.wanted;
  if (wanted === undefined || logTime > wanted) {
    return false;
  }
  const shown = cloud.current?.frame.logTime;
  if (shown === undefined) {
    return true;
  }
  const shownDistance = shown > wanted ? shown - wanted : wanted - shown;
  return wanted - logTime < shownDistance;
}

/**
 * Whether any fetch of the cloud's frames has succeeded — wanted or ahead,
 * shown or not — which is what shows its topic can be read. Every frame that
 * lands is cached, and the cache never evicts the frame it is putting, so it
 * is empty only until the first one lands.
 */
function anyFrameLanded(cloud: components.PointCloud): boolean {
  return cloud.cache.size > 0;
}

function uploadPendingFrame(
  entity: Entity,
  cloud: components.PointCloud,
  state: BabylonState,
) {
  const frame = cloud.pending;
  if (frame) {
    cloud.pending = undefined;
    if (!cloud.mesh || !cloud.material) {
      const built = createPointCloudMesh(
        `${entity.id}.pointCloud`,
        state.context.scene,
      );
      cloud.mesh = built.mesh;
      cloud.material = built.material;
    }
    const shownBefore =
      cloud.current !== undefined && cloud.current.frame.pointCount > 0;
    const shown = frame.pointCount > 0;
    const parentTranslation = new babylon.Vector3(
      frame.parentTranslation.x,
      frame.parentTranslation.y,
      frame.parentTranslation.z,
    );
    let grew = false;
    if (shown) {
      // An empty frame leaves the old buffers in place but hidden: with no
      // vertices Babylon's extent is infinite, which `pointCloudBounds` keeps
      // out of the camera envelope by gating on the shown frame's count.
      uploadPointCloudFrame(cloud.mesh, cloud.material, frame, cloud.spec);
      // The frame's box in the entity's frame: Babylon measured the vertices
      // as they were uploaded, and the parent translation is what they are
      // relative to.
      const box = cloud.mesh.getBoundingInfo().boundingBox;
      grew = components.growExtent(
        cloud,
        box.minimum.add(parentTranslation),
        box.maximum.add(parentTranslation),
      );
    }
    cloud.mesh.setEnabled(shown);
    cloud.current = { frame, parentTranslation };
    // Where playback is, which is what lets the cache drop the frames it has
    // played through and keeps it from evicting the frame on screen.
    cloud.cache.shown(frame.logTime);
    cloud.displayStale = false;
    // The scene changed when the cloud's extent grew, or when the cloud
    // started or stopped showing: an empty frame takes a shown sweep out of
    // the camera envelope, which would otherwise keep the sweep's zoom floor.
    if (grew || shown !== shownBefore) {
      bumpEntityGeneration(state);
    }
    return;
  }

  if (cloud.displayStale && cloud.mesh && cloud.material && cloud.current) {
    applyPointCloudDisplay(
      cloud.mesh,
      cloud.material,
      cloud.current.frame,
      cloud.spec,
    );
    cloud.displayStale = false;
  }
}

/** Marks the scene's content as changed, so the camera envelope re-measures it. */
function bumpEntityGeneration(state: BabylonState) {
  state.sceneConfig.entityGeneration =
    (state.sceneConfig.entityGeneration ?? 0) + 1;
}

function emptyFrame(logTime: bigint): components.PointCloudFrame {
  return {
    logTime,
    pointCount: 0,
    positions: new Float32Array(0),
    measures: new Uint16Array(0),
    measureSpans: components.ZERO_MEASURE_SPANS,
    parentTranslation: { x: 0, y: 0, z: 0 },
    origin: { x: 0, y: 0, z: 0 },
  };
}
